/**
 * RemediationProcess Model
 * @typedef {Object} RemediationProcess
 * @property {number} id
 * @property {number} course_id
 * @property {number} user_id
 * @property {number|null} activity_id
 * @property {number|null} activity_submission_id
 * @property {number|null} course_grade_id
 * @property {string|null} remediation_status
 * @property {string|null} comment
 * @property {number|null} quiz_time_limit
 * @property {Object|null} course - Laravel relationship data
 * @property {Object|null} user - Laravel relationship data
 * @property {Object|null} activity - Laravel relationship data
 * @property {Object|null} activity_submission - Laravel relationship data
 * @property {Object|null} course_grade - Laravel relationship data
 * @property {Array|null} statuses - Laravel relationship data
 * @property {Array|null} logs - Laravel relationship data
 * @property {number|null} course_percentage - Computed property for frontend display
 */
import Model from '@/models/Model.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default class RemediationProcess extends Model {
    static entity = 'remediation_process';

    static modelName = 'RemediationProcess';

    static fields() {
        return {
            id: this.attr(null),
            course_id: this.attr(null),
            user_id: this.attr(null),
            activity_id: this.attr(null),
            activity_submission_id: this.attr(null),
            course_grade_id: this.attr(null),
            remediation_status: this.attr(null),
            comment: this.attr(null),
            quiz_time_limit: this.attr(null),

            // Laravel relationships data
            course: this.attr(null),
            user: this.attr(null),
            activity: this.attr(null),
            activity_submission: this.attr(null),
            course_grade: this.attr(null),
            statuses: this.attr(null),
            logs: this.attr(null),

            // Computed properties for frontend display
            course_percentage: this.attr(null),

            ...super.fields(),

            // VuexORM relationships
            courseRelation: this.belongsTo('courses', 'course_id'),
            userRelation: this.belongsTo('users', 'user_id'),
            activityRelation: this.belongsTo('activities', 'activity_id'),
            courseGradeRelation: this.belongsTo('course_grades', 'course_grade_id'),
            activitySubmissionRelation: this.belongsTo('activity_submissions', 'activity_submission_id'),
        };
    }

    static queryBuilder() {
        return new QueryBuilder('remediation');
    }

    /**
     * Status constants for frontend use
     * @readonly
     */
    static get IN_REMEDIATION_STATUS() {
        return 'In Remediation';
    }
    static get REMEDIATION_PASSED_STATUS() {
        return 'Remediation Passed';
    }
    static get REMEDIATION_FAILED_STATUS() {
        return 'Remediation Failed';
    }
    static get REMEDIATION_GRADED_STATUS() {
        return 'Remediation Graded';
    }
    static get REMEDIATED_GRADE() {
        return 'Remediated Grade';
    }
    static get PENDING_INSTRUCTOR_GRADE_STATUS() {
        return 'Pending Instructor Grade';
    }
    static get FORCED_END_STATUS() {
        return 'Forced End';
    }
    static get IN_PROGRESS_STATUS() {
        return 'In Progress';
    }

    /**
     * Computed property methods for compatibility with existing code
     * @returns {boolean} True if remediation has failed status
     */
    get hasFailingRemediation() {
        return this.remediation_status === RemediationProcess.REMEDIATION_FAILED_STATUS;
    }

    /**
     * @returns {boolean} True if course grade has been remediated
     */
    get hasRemediatedGrade() {
        if (!this.course_grade || !this.course_grade.statuses) return false;
        return this.course_grade.statuses.some(status => status.name === RemediationProcess.REMEDIATED_GRADE);
    }

    /**
     * @returns {boolean} True if remediation is ready to start
     */
    get isReadyToStart() {
        return !this.remediation_status || this.remediation_status === '';
    }

    /**
     * @returns {string} Human-readable status for display
     */
    get displayStatus() {
        if (this.hasRemediatedGrade) return 'Remediation Passed';
        if (this.remediation_status) return this.remediation_status;
        return 'Ready to start';
    }

    /**
     * @returns {string} Bulma CSS class type for status tag
     */
    get displayStatusType() {
        if (this.hasRemediatedGrade) return 'is-success';
        if (this.hasFailingRemediation) return 'is-danger';
        return '';
    }

    /**
     * Activity-related computed properties
     * @returns {string} Activity title or empty string
     */
    get activityTitle() {
        return this.activity?.title || '';
    }

    /**
     * @returns {string} Activity type or empty string
     */
    get activityType() {
        return this.activity?.activity_type || '';
    }

    /**
     * User-related computed properties
     * @returns {string} Full student name or empty string
     */
    get studentName() {
        if (!this.user) return '';
        return `${this.user.first_name} ${this.user.last_name}`.trim();
    }

    /**
     * @returns {string} Student email or empty string
     */
    get studentEmail() {
        return this.user?.email || '';
    }

    /**
     * Course-related computed properties
     * @returns {string} Course title or empty string
     */
    get courseTitle() {
        return this.course?.title || '';
    }

    /**
     * Time limit helpers
     * @returns {number|null} Quiz time limit in minutes or null
     */
    get quizTimeLimitMinutes() {
        return this.quiz_time_limit ? Math.floor(this.quiz_time_limit / 60) : null;
    }

    /**
     * Comment helpers
     * @returns {boolean} True if comment exists and is not empty
     */
    get hasComment() {
        return this.comment && this.comment.trim().length > 0;
    }

    /**
     * @returns {string} Truncated comment (50 chars max) or empty string
     */
    get truncatedComment() {
        if (!this.hasComment) return '';
        return this.comment.length > 50 ? `${this.comment.substring(0, 50)}...` : this.comment;
    }
}
